import { useState } from 'react';
import { Form, Input, Select, Tag } from 'antd';
import { useCountryArray } from '../../../../utils/countryArray';
import { countryCallingCodeOptions } from '../../../../utils/countryCodes';
import { useCountryCodeInfos } from '../utils/useCountryCodeInfos';

const { Option } = Select;
/**
 * A custom hook that returns an array of field objects to be used in a form,
 * along with fetching and setting enumerations for select options in the form.
 *
 * The hook utilizes various hooks like `useState`, `useEffect`, and `useCallback`
 * for managing state and side effects. It also employs `useTranslation` for
 * internationalization and `useAuthContext` and `useErrorMessage` hooks for authentication
 * and error handling respectively.
 * @hook
 *
 * @param {string} entityName - The name of the entity (e.g., 'veterinarian', 'groomer')
 * @returns {Object} - An object containing a fields array with configurations for form fields.
 *
 * @example
 * const { fields } = useFields('veterinarian');
 */
export const useFields = (entityName, { form, country, setCountry } = {}) => {
  const countries = useCountryArray();
  const { handleCountryCodeInfos } = useCountryCodeInfos(country);

  const [cityOptions, setCityOptions] = useState([]);

  const filterOption = (input, option) =>
    option.props.value.toLowerCase().includes(input.toLowerCase());

  const fields = [
    {
      label: 'name',
      name: [entityName, 'name']
    },
    {
      label: 'address.number',
      name: [entityName, 'address', 'number'],
      input: <Input type="number" />
    },
    {
      label: 'address.street',
      name: [entityName, 'address', 'street']
    },
    {
      label: 'address.country',
      name: [entityName, 'address', 'country'],
      input: (
        <Select
          showSearch
          filterOption={filterOption}
          onChange={(value) => {
            if (setCountry) setCountry(value);
            if (form) {
              form.setFieldValue(
                [entityName, 'address', 'postal_code'],
                undefined
              );
              form.setFieldValue([entityName, 'address', 'city'], undefined);
            }
            setCityOptions([]);
          }}
        >
          {(countries || []).map((c) => (
            <Option key={c} value={c}>
              <Tag>{c}</Tag>
            </Option>
          ))}
        </Select>
      )
    },
    {
      label: 'address.postal_code',
      name: [entityName, 'address', 'postal_code'],
      input: (
        <Input
          onChange={(e) => {
            const postalCode = e.target.value;

            if (form) {
              form.setFieldValue([entityName, 'address', 'city'], undefined);
            }
            setCityOptions([]);

            if (postalCode.length < 5) return;

            handleCountryCodeInfos(postalCode).then(({ cities, city }) => {
              if (cities.length > 1) {
                setCityOptions(cities);
              } else {
                if (form) {
                  form.setFieldValue(
                    [entityName, 'address', 'city'],
                    city || undefined
                  );
                }
                setCityOptions([]);
              }
            });
          }}
        />
      )
    },
    {
      label: 'address.city',
      name: [entityName, 'address', 'city'],
      input:
        cityOptions.length > 1 ? (
          <Select
            showSearch
            filterOption={(input, option) =>
              option.children.toLowerCase().includes(input.toLowerCase())
            }
          >
            {cityOptions.map((city) => (
              <Option key={city} value={city}>
                {city}
              </Option>
            ))}
          </Select>
        ) : (
          <Input />
        )
    },
    {
      name: [entityName, 'phone_number'],
      label: 'phone_number.number',
      input: (
        <Input.Group compact>
          <Form.Item
            noStyle
            name={[entityName, 'phone_number', 'country_code']}
            initialValue="+33"
          >
            <Select style={{ width: 90 }} options={countryCallingCodeOptions} />
          </Form.Item>
          <Form.Item noStyle name={[entityName, 'phone_number', 'number']}>
            <Input type="number" style={{ width: 'calc(100% - 90px)' }} />
          </Form.Item>
        </Input.Group>
      )
    }
  ];

  return { fields };
};
